'use client'

import { useContext, useState } from 'react'

import cn from 'clsx'
import { Image } from '@/components/image/image'
import { ImageLightBox } from '@/components/ImageLightBox/ImageLightBox'
import { CLD_FMT_AUTO_VIDEO, useImage } from '@/utils/cloudinary'

import { HelpCenterContext } from '../../HelpCenterContext'
import { parseDimension } from '../../utils/parse-img-size'

import css from './Image.module.css'
import { VideoPlayer } from '@/components/VideoPlayer/VideoPlayer'

export type HCImageProps = React.ImgHTMLAttributes<HTMLImageElement> & {
    lazyload?: boolean
    'data-usezoom'?: string
    usezoom?: string
    'data-screenframe'?: string
    screenframe?: string
    'data-inline'?: string
    'data-nolightbox'?: string
    'data-type'?: 'gif' | 'png' | string
}

export function HCImage({ src, width, height, lazyload, alt = '', ...rest }: HCImageProps) {
    const usezoom =
        typeof rest['data-usezoom'] === 'string' || typeof rest.usezoom === 'string' || false
    const screenframe =
        typeof rest['data-screenframe'] === 'string' ||
        typeof rest.screenframe === 'string' ||
        false
    const inline = typeof rest['data-inline'] === 'string' ? rest['data-inline'] : null
    const noLightbox = typeof rest['data-nolightbox'] === 'string'
    const [showLightBox, setShowLightBox] = useState(false)
    const { imageMaxWidth } = useContext(HelpCenterContext)
    const { getImage } = useImage()

    if (!src) {
        return null
    }

    const hasDataType = 'data-type' in rest
    const isGif = rest['data-type'] === 'gif'

    // Based on the maximum width an image can be displayed,
    // compute the ideal image width and height the image should be transformed to.
    let dimensions: { width: number; height: number } | undefined
    if (width && height) {
        const normWidth = parseDimension(width)
        const normHeight = parseDimension(height)

        if (normWidth && normHeight) {
            if (normWidth > imageMaxWidth) {
                dimensions = {
                    width: imageMaxWidth,
                    height: Math.floor((normHeight * imageMaxWidth) / normWidth),
                }
            } else {
                dimensions = { width: normWidth, height: normHeight }
            }
        }
    }

    const isDevAppEnv =
        import.meta.env.STORYBOOK === 'true' || process.env.NEXT_PUBLIC_APP_ENV === 'development'
    if (isGif && dimensions && !isDevAppEnv) {
        return (
            <VideoPlayer
                src={getImage(src, { format: CLD_FMT_AUTO_VIDEO }).toString()}
                width={dimensions.width}
                height={dimensions.height}
                muted
                playsOnScroll
                playsInline
                loop
                className={css.figure}
            />
        )
    }

    return (
        <>
            {hasDataType && dimensions ? (
                <Image
                    className={cn(css.figure, {
                        [css.useZoom]: usezoom,
                        [css.screenFrame]: screenframe,
                        [css.noLightbox]: noLightbox,
                    })}
                    src={src}
                    alt={alt}
                    onClick={() => !inline && !noLightbox && setShowLightBox(true)}
                    width={dimensions.width}
                    height={dimensions.height}
                    priority={!lazyload}
                    {...rest}
                />
            ) : (
                <>
                    {/* biome-ignore lint/a11y/useAltText: will be provided */}
                    <img
                        className={cn(css.figure, {
                            [css.useZoom]: usezoom,
                            [css.screenFrame]: screenframe,
                            [css.noLightbox]: noLightbox,
                        })}
                        src={src}
                        alt={alt}
                        onClick={() => !inline && !noLightbox && setShowLightBox(true)}
                        loading={lazyload ? 'lazy' : undefined}
                        width={width}
                        height={height}
                        {...rest}
                    />
                </>
            )}

            {showLightBox && (
                <ImageLightBox src={src} captionText={alt} onClose={() => setShowLightBox(false)} />
            )}
        </>
    )
}
